import { useState, useEffect } from "react";
import { Plus, ChevronRight, User, MapPin, Users, Phone, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  HouseholdMemberForm,
  HouseholdMember,
} from "@/components/HouseholdMemberForm";

const EMERGENCY_TYPES = [
  "Earthquake",
  "Flood",
  "Hurricane",
  "Wildfire",
  "Tornado",
  "Winter Storm",
  "Power Outage",
  "Other",
];

interface InterviewFormPanelProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onFormComplete: () => void;
  interviewId?: string;
}

export function InterviewFormPanel({ 
  isCollapsed, 
  onToggle, 
  onFormComplete,
  interviewId 
}: InterviewFormPanelProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isPhaseComplete, setIsPhaseComplete] = useState(false);

  // Basic form state
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("United States");
  const [emergencyTypes, setEmergencyTypes] = useState<string[]>([]);
  
  // Emergency contact
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");

  // Household members - first member is synced to user's name
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([
    {
      id: crypto.randomUUID(),
      name: "",
      age: "",
      health_conditions: "",
      special_needs: "",
    },
  ]);

  // Sync user's name to first household member
  useEffect(() => {
    if (name && householdMembers.length > 0) {
      setHouseholdMembers((prev) =>
        prev.map((member, idx) =>
          idx === 0 ? { ...member, name } : member
        )
      );
    }
  }, [name]);

  // Load existing data if interviewId exists
  useEffect(() => {
    const loadExistingData = async () => {
      if (!interviewId) return;

      const { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('id', interviewId)
        .single();

      if (error) {
        console.error('Error loading interview:', error);
        return;
      }

      if (data) {
        setName(data.name || "");
        const location = data.location as any;
        setAddress(location?.address || "");
        setCity(location?.city || "");
        setState(location?.state || "");
        setCountry(location?.country || "United States");
        setEmergencyTypes(data.emergency_types || []);
        
        if (data.household_members) {
          setHouseholdMembers(data.household_members as any as HouseholdMember[]);
        }
        
        if (data.emergency_contact) {
          const ec = data.emergency_contact as any;
          setEmergencyContactName(ec.name || "");
          setEmergencyContactPhone(ec.phone || "");
        }

        setIsPhaseComplete((data.phase_completed ?? 0) >= 1);
      }
    };

    loadExistingData();
  }, [interviewId]);

  const addHouseholdMember = () => {
    setHouseholdMembers([
      ...householdMembers,
      {
        id: crypto.randomUUID(),
        name: "",
        age: "",
        health_conditions: "",
        special_needs: "",
      },
    ]);
  };

  const updateHouseholdMember = (
    id: string,
    field: keyof HouseholdMember,
    value: string
  ) => {
    setHouseholdMembers(
      householdMembers.map((member) =>
        member.id === id ? { ...member, [field]: value } : member
      )
    );
  };

  const removeHouseholdMember = (id: string) => {
    setHouseholdMembers(householdMembers.filter((member) => member.id !== id));
  };

  const toggleEmergencyType = (type: string) => {
    setEmergencyTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!name || !address || !city || !state || !country) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (emergencyTypes.length === 0) {
        toast({
          title: "Missing Information",
          description: "Please select at least one emergency type",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const validMembers = householdMembers.filter(
        (m) => m.name.trim() && m.age.trim()
      );
      if (validMembers.length === 0) {
        toast({
          title: "Missing Information",
          description: "Please add at least one household member",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!emergencyContactName || !emergencyContactPhone) {
        toast({
          title: "Missing Information",
          description: "Please fill in emergency contact information",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to continue",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const interviewData = {
        user_id: user.id,
        name,
        location: {
          address,
          city,
          state,
          country,
        } as any,
        emergency_types: emergencyTypes,
        household_size: validMembers.length,
        household_members: validMembers as any,
        emergency_contact: {
          name: emergencyContactName,
          phone: emergencyContactPhone,
        } as any,
        property_type: "House",
        status: "in_progress",
        phase_completed: 1,
      };

      if (interviewId) {
        // Update existing interview
        const { error } = await supabase
          .from("interviews")
          .update(interviewData)
          .eq("id", interviewId);

        if (error) throw error;
      } else {
        // Create new interview
        const { error } = await supabase
          .from("interviews")
          .insert(interviewData);

        if (error) throw error;
      }

      toast({
        title: "Information Saved",
        description: "Your information has been saved successfully",
      });

      setIsPhaseComplete(true);
      onFormComplete();
    } catch (error) {
      console.error("Error saving interview:", error);
      toast({
        title: "Error",
        description: "Failed to save your information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Collapsed view - thin strip with icons
  if (isCollapsed) {
    return (
      <div className="h-full w-16 bg-muted/30 border-r border-border flex flex-col items-center py-4 gap-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-10 w-10"
          title="Expand form"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
        
        <Separator className="w-8" />
        
        <div className="flex flex-col gap-4 text-muted-foreground">
          <div title="Personal Info"><User className="h-5 w-5" /></div>
          <div title="Location"><MapPin className="h-5 w-5" /></div>
          <div title="Emergency Types"><AlertTriangle className="h-5 w-5" /></div>
          <div title="Household"><Users className="h-5 w-5" /></div>
          <div title="Emergency Contact"><Phone className="h-5 w-5" /></div>
        </div>
      </div>
    );
  }

  // Expanded view - full form
  return (
    <div className="h-full w-[500px] bg-background border-r border-border flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold">Basic Information</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          disabled={!isPhaseComplete}
          title={isPhaseComplete ? "Collapse form" : "Complete form first"}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Personal Information</h3>
            </div>
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
          </div>

          <Separator />

          {/* Location */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Location</h3>
            </div>
            <div>
              <Label htmlFor="address">Street Address *</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="San Francisco"
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State/Province *</Label>
                <Input
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="CA"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="country">Country *</Label>
              <Input
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              />
            </div>
          </div>

          <Separator />

          {/* Emergency Types */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Primary Emergency Types *</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {EMERGENCY_TYPES.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={type}
                    checked={emergencyTypes.includes(type)}
                    onCheckedChange={() => toggleEmergencyType(type)}
                  />
                  <Label htmlFor={type} className="font-normal cursor-pointer">
                    {type}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Household Members */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Household Members *</h3>
            </div>
            {householdMembers.map((member, index) => (
              <HouseholdMemberForm
                key={member.id}
                member={member}
                index={index}
                onUpdate={updateHouseholdMember}
                onRemove={removeHouseholdMember}
                canRemove={householdMembers.length > 1 && index !== 0}
                isCurrentUser={index === 0}
              />
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addHouseholdMember}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Household Member
            </Button>
          </div>

          <Separator />

          {/* Emergency Contact */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Emergency Contact *</h3>
            </div>
            <div>
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={emergencyContactName}
                onChange={(e) => setEmergencyContactName(e.target.value)}
                placeholder="Jane Doe"
                required
              />
            </div>
            <div>
              <Label htmlFor="contact-phone">Phone Number *</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={emergencyContactPhone}
                onChange={(e) => setEmergencyContactPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Saving..." : isPhaseComplete ? "Update Information" : "Save & Continue to Interview"}
          </Button>
        </form>
      </ScrollArea>
    </div>
  );
}
